// The CRM's app shell (#1201), after the Tailwind Plus sidebar layout: the navigation, rendered
// into the desktop sidebar and the mobile drawer from one list, its count badges, and which
// section is current. app.js owns the routing; this module only draws.
import { icon, solidIcon } from "./icons.js";

// Sections in a group sit in its collapsible list, below the ones used every day (#1596). The
// list's order is the sidebar's and ⌘K's. `key` is the section's ⌘ digit; Tools has none.
export const NAV_SECTIONS = [
  { section: "dashboard", label: "Dashboard", icon: "squares-2x2", key: "1" },
  // Cloud Monitoring's alerts (#2314); the badge counts the open ones. No ⌘ digit, as Families.
  { section: "alerts", label: "Alerts", icon: "bell", badge: "alerts" },
  // What the project costs (#2539); no ⌘ digit, as Families.
  { section: "billing", label: "Billing", icon: "banknotes" },
  { section: "users", label: "Users & Diagnostics", icon: "users", key: "2" },
  // A household by its ID or a member (#1809); no ⌘ digit, so the ones in use since #1596 stay where they are.
  { section: "families", label: "Families", icon: "home" },
  { section: "inbox", label: "Customer Inbox", icon: "inbox", badge: "inbox", key: "3" },
  // One person's messages on every channel, newest first (#1925); no ⌘ digit, as Families.
  { section: "history", label: "Contact History", icon: "clock" },
  { section: "feedback", label: "User Feedback", icon: "chat-bubble-left-right", badge: "feedback", key: "4" },
  { section: "leads", label: "Waitlist Leads", icon: "user-plus", badge: "leads", key: "5" },
  { section: "announcements", label: "System Announcements", icon: "megaphone", badge: "announcements", key: "6" },
  { section: "recipes", label: "Recipes Catalog", icon: "book-open", group: "catalog", key: "7" },
  { section: "ingredients", label: "Master Ingredients", icon: "shopping-bag", group: "catalog", key: "8" },
  { section: "categories", label: "Categories", icon: "tag", group: "catalog", key: "9" },
  { section: "duplicates", label: "Duplicates Resolver", icon: "document-duplicate", badge: "duplicates", group: "tools" },
  { section: "matcher", label: "Matcher Tester", icon: "beaker", group: "tools" },
  { section: "import-cache", label: "Import Cache", icon: "archive-box", group: "tools" },
  { section: "pricing", label: "App Store Pricing", icon: "currency-dollar", group: "tools" },
  { section: "store-search", label: "Store Search", icon: "magnifying-glass", group: "tools" },
  { section: "kroger-check", label: "Kroger Check", icon: "bolt", group: "tools" }
];

export const NAV_GROUPS = {
  catalog: { label: "Catalog", icon: "rectangle-stack" },
  tools: { label: "Tools", icon: "wrench-screwdriver" }
};

// Sections without their own nav item light up their parent's.
const NAV_PARENT = { "recipe-view": "recipes", "recipe-new": "recipes" };

const LINK_CLASSES =
  "group flex items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-paprika-700 " +
  "aria-[current=page]:bg-paprika-50 aria-[current=page]:text-paprika-700 " +
  "dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white dark:aria-[current=page]:bg-white/5 dark:aria-[current=page]:text-white";
// A group's button and its links, after the Tailwind Plus sidebar with expandable sections.
const GROUP_BUTTON_CLASSES =
  "group flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-paprika-700 " +
  "dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white";
const CHEVRON_CLASSES =
  "ml-2 size-5 shrink-0 text-gray-400 group-aria-expanded:rotate-90 group-aria-expanded:text-gray-500 dark:group-aria-expanded:text-gray-400";
const SUBLINK_CLASSES =
  "relative flex items-center gap-x-3 rounded-md py-2 pr-2 pl-9 text-sm/6 text-gray-700 hover:bg-gray-50 hover:text-paprika-700 " +
  "aria-[current=page]:bg-paprika-50 aria-[current=page]:font-semibold aria-[current=page]:text-paprika-700 " +
  "dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white dark:aria-[current=page]:bg-white/5 dark:aria-[current=page]:text-white";
const ICON_CLASSES =
  "size-6 shrink-0 text-gray-400 group-hover:text-paprika-600 group-aria-[current=page]:text-paprika-600 " +
  "dark:group-hover:text-white dark:group-aria-[current=page]:text-white";
// data-tone: neutral (counts), success, danger.
const BADGE_CLASSES =
  "ml-auto min-w-6 rounded-full px-2 py-0.5 text-center text-xs/5 font-medium whitespace-nowrap outline-1 -outline-offset-1 " +
  "bg-white text-gray-600 outline-gray-200 dark:bg-gray-900 dark:text-white dark:outline-white/15 " +
  "data-[tone=success]:bg-green-50 data-[tone=success]:text-green-700 data-[tone=success]:outline-green-600/20 " +
  "dark:data-[tone=success]:bg-green-500/10 dark:data-[tone=success]:text-green-400 dark:data-[tone=success]:outline-green-500/20 " +
  "data-[tone=danger]:bg-red-50 data-[tone=danger]:text-red-700 data-[tone=danger]:outline-red-600/20 " +
  "dark:data-[tone=danger]:bg-red-400/10 dark:data-[tone=danger]:text-red-400 dark:data-[tone=danger]:outline-red-400/20";

// The ⌘ digit, shown while ⌘ (or Ctrl) is held: over a section's icon, in a grouped section's
// indent where the icons would be, and after a closed group's name, so no label gets shorter.
const HINT_CLASSES =
  "flex-none rounded-sm border border-gray-200 bg-white px-1 font-sans text-xs/5 font-normal whitespace-nowrap text-gray-500 " +
  "dark:border-white/10 dark:bg-gray-900 dark:text-gray-400";
const HINT_AT = {
  icon: "absolute top-1/2 left-1/2 -translate-1/2",
  indent: "absolute top-1/2 left-3 -translate-1/2",
  end: "ml-2"
};

function hintHtml(text, at) {
  return text ? `<kbd data-nav-hint class="${HINT_CLASSES} ${HINT_AT[at]}" hidden>⌘${text}</kbd>` : "";
}

function badgeHtml(badge) {
  return badge ? `<span data-nav-badge="${badge}" data-tone="neutral" class="${BADGE_CLASSES}" hidden></span>` : "";
}

/** The sidebar's items. `idPrefix` keeps the group ids apart in the desktop and mobile copies. */
export function navLinksHtml(idPrefix = "nav") {
  const drawn = new Set();
  return NAV_SECTIONS.map(({ section, label, icon: iconName, badge, group, key }) => {
    if (!group) {
      return `<li><a href="#${section}" data-section="${section}" class="${LINK_CLASSES}"><span class="relative flex shrink-0">${icon(iconName, ICON_CLASSES)}${hintHtml(key, "icon")}</span><span class="flex-1 truncate">${label}</span>${badgeHtml(badge)}</a></li>`;
    }
    if (drawn.has(group)) return "";
    drawn.add(group);
    return groupHtml(group, idPrefix);
  }).join("");
}

function groupHtml(group, idPrefix) {
  const { label, icon: iconName } = NAV_GROUPS[group];
  const id = `${idPrefix}-${group}`;
  const members = NAV_SECTIONS.filter((s) => s.group === group);
  const links = members
    .map(({ section, label: linkLabel, badge, key }) =>
      `<li><a href="#${section}" data-section="${section}" class="${SUBLINK_CLASSES}"><span class="flex-1 truncate">${linkLabel}</span>${badgeHtml(badge)}${hintHtml(key, "indent")}</a></li>`)
    .join("");
  // A closed group shows its members' digits on its own row: ⌘7–9.
  const keys = members.map((s) => s.key).filter(Boolean);
  const range = keys.length > 1 ? `${keys[0]}–${keys.at(-1)}` : keys[0];
  return `<li data-nav-group="${group}"><button type="button" command="--toggle" commandfor="${id}" aria-expanded="false" class="${GROUP_BUTTON_CLASSES}">${icon(iconName, ICON_CLASSES)}<span class="flex-1 truncate">${label}</span>${hintHtml(range, "end")}${solidIcon("chevron-right", CHEVRON_CLASSES)}</button>` +
    `<el-disclosure id="${id}" hidden class="contents"><ul role="list" class="mt-1 space-y-1 px-2">${links}</ul></el-disclosure></li>`;
}

export function renderNav() {
  document.querySelectorAll("[data-nav]").forEach((list, index) => {
    list.innerHTML = navLinksHtml(`nav${index}`);
  });
}

export function setActiveNav(sectionName) {
  const current = NAV_PARENT[sectionName] || sectionName;
  document.querySelectorAll("[data-nav] [data-section]").forEach((link) => {
    if (link.dataset.section === current) link.setAttribute("aria-current", "page");
    else link.removeAttribute("aria-current");
  });
  // Opens the group holding the current section; a group opened or closed by hand stays so.
  document.querySelectorAll("[data-nav-group]").forEach((item) => {
    if (item.querySelector("[aria-current=page]")) item.querySelector("el-disclosure")?.removeAttribute("hidden");
  });
}

/** The section ⌘ + `digit` goes to, or undefined. */
export function sectionForKey(digit) {
  return NAV_SECTIONS.find((s) => s.key === digit)?.section;
}

/**
 * ⌘1–⌘9 (Ctrl on other keyboards, as ⌘K) go to a section; holding ⌘ alone for a moment shows
 * each section's digit in the sidebar. Not while a dialog other than the mobile drawer is open,
 * since leaving the screen would drop what it holds.
 */
export function initNavShortcuts(goToSection) {
  const HOLD_MS = 300;
  let timer = null;
  const blocked = () =>
    document.getElementById("app")?.hidden || [...document.querySelectorAll("dialog[open]")].some((d) => d.id !== "mobileSidebar");
  const showHints = (show) => {
    clearTimeout(timer);
    timer = null;
    document.querySelectorAll("[data-nav-hint]").forEach((hint) => {
      const group = hint.closest("button")?.closest("[data-nav-group]");
      hint.hidden = !show || Boolean(group && !group.querySelector("el-disclosure")?.hidden);
    });
  };

  document.addEventListener("keydown", (e) => {
    if ((e.key === "Meta" || e.key === "Control") && !e.shiftKey && !e.altKey && !e.repeat) {
      if (!blocked()) timer = setTimeout(() => showHints(true), HOLD_MS);
      return;
    }
    showHints(false);
    const digit = /^Digit([1-9])$/.exec(e.code)?.[1];
    const section = digit && sectionForKey(digit);
    if (!section || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || blocked()) return;
    e.preventDefault();
    goToSection(section);
  });
  document.addEventListener("keyup", (e) => {
    if (e.key === "Meta" || e.key === "Control") showHints(false);
  });
  window.addEventListener("blur", () => showHints(false));
}

/** Shows `text` on every copy of a nav badge (desktop and mobile); empty or 0 hides it. */
export function setNavBadge(name, text, tone = "neutral") {
  const show = text !== null && text !== undefined && text !== "" && text !== 0;
  document.querySelectorAll(`[data-nav-badge="${name}"]`).forEach((badge) => {
    badge.hidden = !show;
    if (show) {
      badge.textContent = String(text);
      badge.dataset.tone = tone;
    }
  });
}

export function closeMobileSidebar() {
  const dialog = document.getElementById("mobileSidebar");
  const wrapper = dialog?.closest("el-dialog");
  if (dialog?.open) {
    if (typeof wrapper?.hide === "function") wrapper.hide({ restoreFocus: false });
    else dialog.close();
  }
}
